<template>
  <div class="my-6 table-wrapper overflow-x-auto shadow rounded-xl mt-10">
    <div class="mt-6" v-if="isLoading">
      <spinner
        size="medium"
        :spacing="40"
        message=""
        :line-fg-color="$primaryColor"
      />
    </div>
    <div class="px-4" v-else>
      <div class="py-4 flex items-center justify-between">
        <h3 class="text-root-2 font-semibold">All PayLinks</h3>
        <div
          class="flex items-center gap-x-2 rounded text-center text-sm font-normal shadow-sm bg-root-3 px-4 py-2 border border-root-3 cursor-pointer text-root-2"
          @click="showDialog('showTableFilter')"
        >
          <filter-icon />
          <span>Filter</span>
        </div>
      </div>
      <el-table
        class="relative cursor-pointer py-5 font-normal"
        index
        :data="tableData"
        style="width: 100%"
      >
        <el-table-column label="LINK NAME" class="w-full" :width="columnWidth">
          <template slot-scope="scope">
            <div class="font-light text-xs md:text-sm">
              {{ scope.row.title }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="LINKS" class="w-full" :width="columnWidth">
          <template slot-scope="scope">
            <div
              class="font-semibold flex text-blue-3 text-sm md:text-sm underline"
            >
              <span
                @click="previewLink(scope.row.url)"
                class="truncate text-xs"
                >{{ scope.row.url }}</span
              >
              <span
                class="w-full justify-center cursor-pointer flex items-center"
                v-clipboard:copy="scope.row.url"
                v-clipboard:success="onCopy"
                v-clipboard:error="onError"
              >
                <copy-icon />
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          label="STATUS"
          class="w-full"
          :width="columnWidth"
          align="center"
        >
          <template slot-scope="scope">
            <div class="font-bold flex items-center justify-center gap-x-1">
              <span
                class="h-2 w-2 block rounded-full"
                :class="
                  scope.row.status === 'active' && scope.row.isValid
                    ? 'bg-green-1'
                    : 'bg-gray-2'
                "
              ></span>
              <span class="font-semibold text-xs md:text-sm capitalize">{{
                scope.row.status === 'active' && scope.row.isValid
                  ? scope.row.status
                  : 'Inactive'
              }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          label="DATE CREATED"
          class="w-full"
          :width="columnWidth"
        >
          <template slot-scope="scope">
            <div class="font-semibold text-xs md:text-sm">
              <el-tooltip
                :content="tooltipDateContent(scope.row.createdAt)"
                placement="top"
                effect="light"
              >
                <span>{{ parseRelativeTime(scope.row.createdAt) }}</span>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          label="TYPE"
          class="w-full"
          :width="columnWidth"
          align="center"
        >
          <template slot-scope="scope">
            <div class="flex items-center">
              <div
                class="text-xs md:text-sm capitalize w-full p-2 rounded flex items-center justify-center shadow-sm"
                :class="statusClass(scope.row.type)"
              >
                {{ formatServerName(scope.row.type) }}
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column class="w-full" :width="columnWidth" align="center">
          <template slot-scope="scope">
            <div class="flex items-center gap-x-3">
              <span @click="paymentLinkDetails(scope.row.title, scope.row.id)">
                <eye-icon />
              </span>
              <span
                v-if="scope.row.isValid"
                @click="showDialog('showPaymentLinkForm', 'edit', scope.row)"
              >
                <edit-pen-icon-2 />
              </span>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <transaction-empty-state use="receive-payment" v-if="noTransactions">
      <h3 class="font-semibold text-root-2">No Payment Links</h3>
      <p
        class="font-light text-root-4 text-xs flex items-center justify-center mt-2"
      >
        <span class="w-3/4 text-center"
          >Your payment links will show up here once you create one</span
        >
      </p>
      <o-button
        class="bg-primary rounded-3xl text-white text-xs my-5"
        @click="goToCreatePaymentLink"
        >+ New Payment Link</o-button
      >
    </transaction-empty-state>
    <div class="p-6 grid grid-cols-1 md:justify-items-end">
      <!--Pagination-->
      <el-pagination
        @current-change="handleCurrentChange"
        :page-size="payload.size"
        @next-click="next"
        @prev-click="previous"
        background
        layout="prev, pager, next"
        :total="page.totalCount"
      >
      </el-pagination>
      <!-- dialogs -->
      <payment-link-form
        v-if="showPaymentLinkForm"
        :showDialog="showPaymentLinkForm"
        :mode="mode"
        @successful="paymentLinkCreated"
        @closeDialog="closeDialog('showPaymentLinkForm')"
      />
      <payment-link-created
        v-if="showPaymentLinkCreated"
        :showDialog="showPaymentLinkCreated"
        @closeDialog="closeDialog('showPaymentLinkCreated')"
        :view="mode"
      />
      <table-filter
        v-if="showTableFilter"
        :showDialog="showTableFilter"
        @filterParams="searchFilterParams"
        @closeDialog="closeDialog('showTableFilter')"
      />
    </div>
  </div>
</template>

<script>
import statusClass from '@/mixins/app.js'
import getPaymentLinks from '@/mixins/paymentLink.js'
import breakpoints from '@/utils/breakpoints.js'

import { FormatServerName, ParseRelativeTime } from '@/utils/helpers.js'
export default {
  mixins: [statusClass, getPaymentLinks],
  props: {
    tableData: {
      type: Array,
      default: () => []
    },
    isPaginate: {
      type: Boolean,
      default: false
    },
    total: {
      type: Number,
      default: 0
    },
    loading: { type: Boolean, default: false }
  },
  components: {
    TransactionEmptyState: () =>
      import('@/components/empty-states/TransactionEmptyState.vue'),
    PaymentLinkForm: () =>
      import('@/components/receivePayment/dialogs/PaymentLinkForm.vue'),
    FilterIcon: () => import('@/assets/icons/FilterIcon.vue'),
    TableFilter: () =>
      import('@/components/receivePayment/dialogs/TableFilter.vue'),
    PaymentLinkCreated: () =>
      import('@/components/receivePayment/dialogs/PaymentLinkCreated'),
    EditPenIcon2: () => import('@/assets/icons/EditPenIcon2.vue'),
    EyeIcon: () => import('@/assets/icons/EyeIcon.vue'),
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue')
  },
  data () {
    return {
      payload: {
        page: 1,
        size: 5,
        status: '',
        type: '',
        from: '',
        to: '',
        sort_direction_desc: true,
        sort_by: 'created_at'
      },
      showPaymentLinkForm: false,
      showTableFilter: false,
      showPaymentLinkCreated: false,
      mode: '',
      isLoading: this.loading
    }
  },
  methods: {
    parseRelativeTime (timeToPick) {
      return ParseRelativeTime(timeToPick)
    },
    searchFilterParams (filterParams) {
      this.payload.status = filterParams.status ? filterParams.status : ''
      this.payload.type = filterParams.paymentLinkType
        ? filterParams.paymentLinkType
        : ''
      this.applyDateFilter(filterParams)
      this.getPaymentLinks()
    },
    formatServerName (val) {
      return FormatServerName(val)
    },
    next () {
      this.payload.page++
      this.getPaymentLinks()
    },
    previous () {
      this.payload.page--
      this.getPaymentLinks()
    },
    handleCurrentChange (val) {
      this.payload.page = val
      this.getPaymentLinks()
    },
    closeDialog (dialog) {
      this[dialog] = false
    },
    showDialog (dialog, mode, details) {
      if (mode === 'edit') {
        this.$store.dispatch(
          'paymentLink/setCurrentPaymentLinkDetails',
          details
        )
      }
      this.mode = mode
      this[dialog] = true
    },
    goToCreatePaymentLink () {
      this.$router.push({
        path: '/payment-link',
        query: {
          mode: 'create'
        }
      })
    },
    paymentLinkCreated (view) {
      this.mode = view
      this.showPaymentLinkCreated = true
    },
    paymentLinkDetails (title, id) {
      this.$store.dispatch('paymentLink/setCurrentPaymentLinkId', id)
      this.$router.push(`/payment-link-details/${id}`)
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Payment Link Copied'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    previewLink (url) {
      window.open(url)
    }
  },
  computed: {
    page () {
      return this.$store.state.paymentLink.paymentLinks.page
    },
    noTransactions () {
      return !this.tableData.length > 0
    },
    columnWidth () {
      return breakpoints.lgAndUp ? 'auto' : breakpoints.mdAndUp ? '220' : '170'
    }
  }
}
</script>

<style scoped>
.table-wrapper {
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.12),
    0px 2px 4px rgba(3, 2, 7, 0.12);
  border-radius: 10.79px;
}

>>> .el-table th {
  background: var(--background);
  border: none;
  color: var(--text-4);
  font-weight: 500;
}
.el-button:hover {
  color: white;
  opacity: 0.8;
  border-color: #c7c8d2;
  background-color: #f26722;
  border: none;
}
>>> li.number.active {
  background-color: var(--background) !important;
  border: 0.5px solid var(--color-primary) !important;
  box-sizing: border-box;
  border-radius: 100px;
  font-weight: 400;
  color: var(--color-primary) !important ;
}
>>> li.number {
  background-color: var(--background) !important;
  border: 0.5px solid var(--text-1) !important;
  box-sizing: border-box;
  border-radius: 100px !important;
  font-weight: 400;
  /* color: var(--color-primary) !important ; */
}
>>> .el-table__empty-text {
  display: none;
  height: 0;
}
>>> .el-table__empty-block {
  height: 0 !important;
  min-height: 0;
  background: red;
}
>>> .el-table .el-table__cell {
  padding: 8px 0;
}
@media screen and (max-width: 768px) {
  >>> .el-table th.el-table__cell > .cell {
    font-size: 12px;
    word-break: normal;
  }
}
</style>
